import React, { useRef, useState } from 'react';

import { Box } from '@mui/material';

const MIN_TABLE_WIDTH_PX = 480;
const KEYBOARD_RESIZE_STEP_PX = 16;
const STORAGE_KEY = 'searchRankingTableFrame';
const KEY_DELTA: Record<string, number> = { ArrowLeft: -KEYBOARD_RESIZE_STEP_PX, ArrowRight: KEYBOARD_RESIZE_STEP_PX };

type Edge = 'left' | 'right';
type TableFrame = { width: number; offset: number };

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const storageError = (error: unknown) => console.warn(`Error using localStorage key "${STORAGE_KEY}":`, error);

function readStoredFrame(): TableFrame | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { width, offset } = JSON.parse(raw) as Partial<TableFrame>;
    if (Number.isFinite(width) && Number.isFinite(offset) && width >= MIN_TABLE_WIDTH_PX && offset >= 0) {
      return { width, offset };
    }
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    storageError(error);
  }
  return null;
}

function writeStoredFrame(frame: TableFrame | null) {
  try {
    if (frame) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(frame));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    storageError(error);
  }
}

function frameFromDelta(edge: Edge, start: TableFrame, delta: number, containerWidth: number): TableFrame {
  const minWidth = Math.min(MIN_TABLE_WIDTH_PX, containerWidth);
  const offset =
    edge === 'left' ? clamp(start.offset + delta, 0, Math.max(0, start.offset + start.width - minWidth)) : start.offset;
  const width =
    edge === 'right'
      ? clamp(start.width + delta, minWidth, Math.max(minWidth, containerWidth - start.offset))
      : start.width - (offset - start.offset);
  return { width, offset };
}

const keptFrame = (frame: TableFrame, containerWidth: number) =>
  frame.offset <= 1 && frame.width >= containerWidth - 2 ? null : frame;

const handleSx = (edge: Edge) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  [edge]: -8,
  zIndex: 2,
  width: 16,
  cursor: 'ew-resize',
  touchAction: 'none',
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
  '&::before': { content: '""', position: 'absolute', inset: '0 6px', bgcolor: 'divider', borderRadius: 1 },
  '&:hover::before, &:focus-visible::before': { bgcolor: 'primary.main' },
});

export default function ResizableTableFrame({ children }: { children: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState<TableFrame | null>(readStoredFrame);
  const frameStateRef = useRef(frame);
  const commit = (nextFrame: TableFrame | null) => {
    frameStateRef.current = nextFrame;
    setFrame(nextFrame);
    writeStoredFrame(nextFrame);
  };
  const measure = () => {
    const container = containerRef.current;
    const frameNode = frameRef.current;
    if (!container || !frameNode) return null;
    const current = frameStateRef.current;
    return {
      container,
      frameNode,
      containerWidth: container.getBoundingClientRect().width,
      start: { width: current?.width ?? frameNode.getBoundingClientRect().width, offset: current?.offset ?? 0 },
    };
  };
  const resizeBy = (edge: Edge, delta: number) => {
    const measured = measure();
    if (!measured) return;
    commit(keptFrame(frameFromDelta(edge, measured.start, delta, measured.containerWidth), measured.containerWidth));
  };
  const beginResize = (edge: Edge, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const measured = measure();
    if (!measured) return;
    const { container, frameNode, containerWidth, start } = measured;
    let latest = start;
    const { style } = document.body;
    const previousStyle = { cursor: style.cursor, userSelect: style.userSelect };
    Object.assign(style, { cursor: 'ew-resize', userSelect: 'none' });
    const onMove = (moveEvent: MouseEvent) => {
      latest = frameFromDelta(edge, start, moveEvent.clientX - event.clientX, containerWidth);
      Object.assign(frameNode.style, { width: `${latest.width}px`, marginLeft: `${latest.offset}px` });
    };
    const onUp = () => {
      Object.assign(style, previousStyle);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      Object.assign(frameNode.style, { width: '', marginLeft: '' });
      commit(keptFrame(latest, container.getBoundingClientRect().width));
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%', mt: 2 }}>
      <Box
        ref={frameRef}
        sx={{
          position: 'relative',
          // Numeric ml is theme spacing, so 80 would become 640px and slide the table off the page.
          width: frame ? `${frame.width}px` : '100%',
          ml: frame ? `${frame.offset}px` : 0,
        }}
      >
        {children}
        {(['left', 'right'] as const).map((edge) => (
          <Box
            key={edge}
            role="separator"
            tabIndex={0}
            aria-orientation="vertical"
            aria-valuemin={MIN_TABLE_WIDTH_PX}
            aria-valuenow={frame?.width}
            aria-label={`Resize the table from the ${edge} edge. Use the left and right arrow keys.`}
            onMouseDown={(mouseEvent) => beginResize(edge, mouseEvent)}
            onKeyDown={(keyEvent) => {
              if (!KEY_DELTA[keyEvent.key]) return;
              keyEvent.preventDefault();
              resizeBy(edge, KEY_DELTA[keyEvent.key]);
            }}
            sx={handleSx(edge)}
          />
        ))}
      </Box>
    </Box>
  );
}
